import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  activateBusinessUnit,
  createBusinessUnit,
  deactivateBusinessUnit,
  fetchBusinessUnitLookup,
  fetchBusinessUnits,
  updateBusinessUnit,
} from '../api/business-unit.api';

// Root key — both the full list and the picker lookup live under it, so one invalidation on a
// mutation refreshes both (a deactivate must drop the unit out of every picker immediately).
const BU_KEY = ['business-units'];
const BU_LOOKUP_KEY = [...BU_KEY, 'lookup'];

// Full list, including inactive units — the admin list page and list-page filters (you must
// still be able to filter by a since-deactivated unit). See useBusinessUnitLookup() for pickers.
export function useBusinessUnits() {
  return useQuery({ queryKey: BU_KEY, queryFn: fetchBusinessUnits });
}

// Active-only — the shared data source for every Business Unit picker. `staleTime: 0` because
// form dialogs stay mounted for the page's lifetime, so a cached list would keep offering a
// unit somebody else deactivated in the meantime.
export function useBusinessUnitLookup() {
  return useQuery({ queryKey: BU_LOOKUP_KEY, queryFn: fetchBusinessUnitLookup, staleTime: 0 });
}

export function useCreateBusinessUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBusinessUnit,
    onSuccess: () => qc.invalidateQueries({ queryKey: BU_KEY }),
  });
}

export function useUpdateBusinessUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{ name: string; description?: string | null; departmentId: string; headId?: string | null }>;
    }) => updateBusinessUnit(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: BU_KEY }),
  });
}

export function useDeactivateBusinessUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deactivateBusinessUnit,
    onSuccess: () => qc.invalidateQueries({ queryKey: BU_KEY }),
  });
}

export function useActivateBusinessUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: activateBusinessUnit,
    onSuccess: () => qc.invalidateQueries({ queryKey: BU_KEY }),
  });
}
